import { useId, useState } from "react";
import { barrierQuestionText, barrierRuleText } from "../lib/barrierCopy";
import type { Difficulty } from "../lib/labeling";
import { useIntroStore } from "../state/introStore";

/**
 * The trend Loop's pre-guess instruction.
 *
 * It used to lead with the mechanics: "Barriers sit ±2.0× ATR from entry". A
 * player who has just dismissed the intro has no reason to know what an ATR
 * is, and the first thing the game asks them should not be the first thing
 * they have to look up. So the question comes first, in the app's foreground
 * colour, and the formula sits behind a help control for whoever wants it.
 *
 * The control is a real button rather than a hover tooltip because half the
 * players are on a phone, where there is no hover. The explanation ends with a
 * way back into the intro, which is otherwise a one-time dialog with no door.
 */
export function BarrierRule({ difficulty }: { difficulty: Difficulty }) {
  const [open, setOpen] = useState(false);
  const showIntro = useIntroStore((s) => s.show);
  const explanationId = useId();

  return (
    <div className="flex max-w-md flex-col items-center gap-1 text-center">
      <p className="text-sm text-fg">{barrierQuestionText()}</p>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={explanationId}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        // A dotted underline so it reads as something to press rather than as
        // a second line of instruction, without the weight of a bordered
        // control next to the two guess buttons.
        className="text-xs text-dim underline decoration-dotted decoration-dim/60 underline-offset-4 transition-colors hover:text-accent"
      >
        how are the lines set?
      </button>
      {open && (
        <p id={explanationId} className="text-xs text-muted">
          {barrierRuleText(difficulty)}{" "}
          <button
            type="button"
            onClick={showIntro}
            className="text-accent transition-colors hover:text-accent/70"
          >
            show intro again
          </button>
        </p>
      )}
    </div>
  );
}
